/**
 * A farm in the Chianti hills (an agriturismo: a working farm that feeds its visitors): sheep on the hill, olives,
 * vines, the cantina under the house, lunch at the long table under the pergola. You come for the day: help Beppe the
 * shepherd bring the flock in (he pays you, and remembers you), taste the wine, eat, lie under an olive tree.
 * Prices are Italian ones (euros), shown in the money this life sees.
 */
import { cheaper } from './prices.ts';
import { advance, earn, message, spend, type LifeEvent, type LifeState } from './life.ts';
import { EUR_NGN } from './travel.ts';

export const FARM = { name: 'Podere Le Querce, Chianti', short: 'Podere Le Querce', shepherd: 'Beppe' } as const;
const euro = (n: number) => Math.round(n * EUR_NGN);
export const farmPrices = { lunch: cheaper(euro(38)), tasting: cheaper(euro(25)) } as const;

export type FarmThing = 'lunch' | 'tasting' | 'picnic' | 'sheep' | 'animals';

/** Something to do on the farm: what it costs, what it does, the line for it. */
export function atFarm(s: LifeState, what: FarmThing): { ok: boolean; line: string; events: LifeEvent[] } {
  const need = (k: 'fun' | 'energy' | 'social' | 'hygiene' | 'hunger', v: number) => { s.needs[k] = Math.max(0, Math.min(100, s.needs[k] + v)); };
  let line = '', mins = 15;
  if (what === 'lunch') {
    if (!spend(s, farmPrices.lunch, 'Lunch at the agriturismo')) return { ok: false, line: "Lunch is a set menu, and you can't afford it.", events: [] };
    mins = 90; need('hunger', 45); need('social', 14); need('fun', 8);
    line = 'Pici with wild boar ragù, then the farm\'s own pecorino with honey, bread from the wood oven, a jug of red. Strangers at the long table are friends by the coffee.';
  } else if (what === 'tasting') {
    if (!spend(s, farmPrices.tasting, 'Wine tasting')) return { ok: false, line: "The tasting is paid, and you can't afford it.", events: [] };
    mins = 45; need('fun', 14); need('social', 8); need('energy', -3);
    line = 'Down into the cool of the cantina: oak barrels, a candle, four glasses. The young Chianti, the Riserva, a Vin Santo with cantucci to dip.';
  } else if (what === 'picnic') {
    mins = 60; need('energy', 12); need('fun', 10);
    line = 'A blanket under the oldest olive tree: bread, cheese, a few figs. The hills go blue in the distance; somewhere a bell rings the hour.';
  } else if (what === 'sheep') {
    mins = 5; need('fun', 5);
    line = 'The ewe lets you scratch behind her ears, then goes back to the grass as if you were never there.';
  } else {
    mins = 10; need('fun', 6);
    line = 'The cows lift their heads to look at you, chewing, and decide you are not interesting.';
  }
  return { ok: true, line, events: advance(s, mins, { busy: true }) };
}

/** The flock's in: Beppe pays you for the afternoon (and next time he's glad to see you). */
export function flockIn(s: LifeState, sheep: number, minutes: number): { line: string; events: LifeEvent[] } {
  s.stats.flocks = (s.stats.flocks ?? 0) + 1;
  const first = s.stats.flocks === 1, pay = euro(first ? 40 : 30), events: LifeEvent[] = [];
  earn(s, pay, `${FARM.shepherd}, for bringing the flock in`);
  s.needs.fun = Math.min(100, s.needs.fun + 12); s.needs.energy = Math.max(0, s.needs.energy - 10);
  if (first) {
    s.history.push({ t: s.minutes, text: `Brought a flock of ${sheep} sheep in off the hill in Chianti.` });
    message(s, FARM.shepherd, 'Complimenti! Not bad for someone from the city. Come back in the spring for the lambs.', events);
  }
  return { line: `All ${sheep} in, the gate shut behind the last one. ${FARM.shepherd} counts them twice, nods, and presses some notes into your hand.`, events: [...events, ...advance(s, Math.max(10, Math.round(minutes)), { busy: true })] };
}
